import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BottomDrawer from '../../components/common/BottomDrawer';
import { InputBackOffice } from '../../components/common/InputBackoffice';
import Spinner from '../../components/common/Spinner';
import { ErrorToast } from '../../components/common/Toasts/Toasts';
import check from '../../assets/icons/check.svg';
import search from '../../assets/icons/search.svg';
import { P2, P3 } from '../../styles/FontStyles';
import {
  ArrowPolygon,
  AuthLayoutTitle,
  Select,
  ToggleTabButton,
  ToggleTabs
} from '../../styles/GlobalComponents';
import {
  FlexCenter,
  Label,
  SInput,
  SInputContainer
} from '../../styles/stylesStyled';
import { CreationTypes } from '../../types/loginTypes';
import { RouteComponentProps } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Button } from '../../components';
import countries from '../../lib/countries';
import { colors } from '../../styles/GlobalStyles';
import {
  updateProfileDetailsWithoutWallet,
  updateProfileDetailsWithWallet
} from './ProfileDetails.actions';
import moment from 'moment';
import { useAccount } from 'wagmi';

const captchaId = process.env.REACT_APP_CAPTCHA_ID;

const ProfileDetails = ({ history }: RouteComponentProps) => {
  const { t } = useTranslation('onboarding');

  const dispatch = useAppDispatch();

  const { loading, register, userInfo } = useAppSelector((state) => state.auth);

  const { referrerId, approvalDate, walletAddress, walletAddressSign } =
    register;

  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [country, setCountry] = useState('');
  const [userCountry] = useState('');
  const [countryCode, setCountryCode] = useState('');

  const [countryList, setCountryList] = useState(countries);
  const [countryImage, setCountryImage] = useState('');

  const [isVisible, setIsVisible] = useState(false);

  const { address, isConnected, connector } = useAccount();

  const onClose = useCallback(() => {
    setIsVisible(false);
  }, []);

  const confirm = () => {
    if (!isConnected) {
      window?.initGeetest4(
        {
          captchaId,
          language: 'eng',
          product: 'bind',
          riskType: 'slide',
          nativeButton: {
            width: '100%',
            marginBottom: '20px'
          }
        },
        function (captcha: any) {
          captcha.showBox();
          captcha
            .onReady(function () {
              setCaptchaLoading(false);
            })
            .onSuccess(function () {
              const result = captcha.getValidate();

              dispatch(
                updateProfileDetailsWithoutWallet({
                  referrerId: referrerId ? referrerId : userInfo?.user?.id,
                  username,
                  approvalDate: approvalDate
                    ? approvalDate
                    : moment().utc().format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'),
                  email,
                  country,
                  countryCode,
                  result,
                  history
                })
              );
            })
            .onError(function () {
              setCaptchaLoading(false);
            });
        }
      );
    } else {
      if (!username) {
        ErrorToast(t('profile_details.err_please_add_username'));
      } else {
        dispatch(
          updateProfileDetailsWithWallet({
            referrerId: referrerId ? referrerId : userInfo?.user?.id,
            username,
            approvalDate: approvalDate
              ? approvalDate
              : moment().utc().format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'),
            email,
            country,
            countryCode,
            walletAddress,
            walletAddressSign,
            history
          })
        );
      }
    }
  };

  const checkCountry = (country: any) => {
    setCountryList(
      countryList.map((item) =>
        item.name === country.name
          ? {
              ...item,
              checked: true
            }
          : {
              ...item,
              checked: false
            }
      )
    );
  };

  const Required = () => {
    return <span style={{ color: colors.error500 }}>*</span>;
  };

  return (
    <>
      {(loading || captchaLoading) && <Spinner fullScreen={true} transparent />}

      <AuthLayoutTitle>Welcome to Big Hearts!</AuthLayoutTitle>

      <P3 center mb='40'>
        BASIC INFORMATIONS
      </P3>

      <Label>
        {t('profile_details.usernameLabel')}
        <Required />
      </Label>

      <InputBackOffice
        id={'username'}
        type={'text'}
        placeholder={t('profile_details.usernamePlaceholder')}
        name={'username'}
        value={username.replace(/[^A-Z0-9]/gi, '') || ''}
        onChange={(e: string) =>
          setUsername(e.replace(/[^A-Z0-9]/gi, '').toLowerCase())
        }
        label={t('profile_details.usernameLabel')}
      />

      <Label>
        Email <Required />
      </Label>

      <InputBackOffice
        id={'email'}
        type={'text'}
        placeholder={t('email_address.enter_your_email_placeholder')}
        name={'email'}
        value={email.replace(/\s+/g, '')}
        onChange={(e: string) => setEmail(e.replace(/\s+/g, ''))}
        label={t('email_address.label')}
      />

      <div>
        <Select
          onClick={() => {
            setIsVisible(true);
          }}
          style={{ cursor: 'pointer' }}>
          <FlexCenter>
            {countryImage && (
              <img
                src={countryImage}
                alt='flag'
                style={{
                  marginRight: '20px',
                  width: '28px',
                  height: '20px'
                }}
              />
            )}

            <div>{country || t('profile_details.countryLabel')}</div>
          </FlexCenter>
          <ArrowPolygon direction={''} />
        </Select>
      </div>

      <Button
        m='30px 0 10px 0'
        disabled={username === '' || email === '' || countryCode === ''}
        onClick={confirm}>
        {t('profile_details.continue')}
      </Button>

      <BottomDrawer isVisible={isVisible} onClose={onClose} height={'90vh'}>
        <SInputContainer>
          <img src={search} alt='search.svg' />
          <SInput
            placeholder={t('profile_details.countryLabelPlaceholder')}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SInputContainer>
        {countryList &&
          countryList
            ?.sort((a) => (a.name === userCountry ? -1 : 1))
            .filter((value) => {
              if (searchTerm === '') {
                return value;
              } else if (
                value.name.toLowerCase().includes(searchTerm.toLowerCase())
              ) {
                return value;
              } else return null;
            })
            .map((country, i) => {
              if (country.hidden) return null;
              return (
                <div
                  onClick={() => {
                    setCountry(country.name);
                    setCountryCode(country.code);
                    setCountryImage(country.flag);
                    setIsVisible(false);
                    checkCountry(country);
                  }}
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '20px'
                    }}>
                    <img
                      src={country.flag}
                      alt='flag'
                      style={{
                        marginRight: '20px',
                        width: '28px',
                        height: '20px'
                      }}
                    />

                    <P2>{country.name}</P2>
                  </div>
                  {country.checked && (
                    <img
                      src={check}
                      alt='check'
                      style={{ marginBottom: '30px' }}
                    />
                  )}
                </div>
              );
            })}
      </BottomDrawer>
    </>
  );
};

export default ProfileDetails;
